// src/App.js
import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, Outlet, useNavigate } from 'react-router-dom';

import { getBasePath } from './utils/getBasePath';
import { LiveKitProvider } from './contexts/LiveKitContext';
import { ProtectedRoute, TopBar, AppSidebar } from './components';
import { SidebarInset, SidebarProvider } from '@slai/ui';
import {
    // Main pages
    Dashboard,
    AgenticWorkflow,
    AgenticWorkflowDetailLayout,
    // CallAnalyticsPage,
    CallAnalyticsPageNew,
    // SMSAnalyticsPage,
    SMSAnalyticsPageNew,
    LiveMonitorPage,
    WorkflowPhoneNumbersPage,
    ContactManager,
    AgenticWorkflowBuilder,
    AgentCommandCenter,
    ManagerCommandCenter,
    AccessManager,
    SuperAdminDataEntry,
    // SMSManagement,
    PhoneNumberManager,
    TenantConfigViewer,
    CompanyProducts,
    People,
    // Auth pages
    Login,
    Terms,
    Privacy,
    DemoRequest,
    NoPlatformAccess,
    // Error page
    ErrorPage,
} from './pages';

import { ToastContainer, Slide } from 'react-toastify';
import { navigationService } from './services/api/config/navigationService';
import { NO_PLATFORM_ACCESS_PATH } from './utils/platformAccess';

// Component to set up navigation service
const NavigationSetup = () => {
    const navigate = useNavigate();

    useEffect(() => {
        navigationService.setNavigator(navigate);
    }, [navigate]);

    return null;
};

const App = () => {
    const routerBasename = getBasePath() || '/';

    return (
        <Router basename={routerBasename}>
            <NavigationSetup />
            <ToastContainer transition={Slide} hideProgressBar theme='light' autoClose={3000} />
            <Routes>
                {/* Public routes */}
                <Route path='/login' element={<Login />} />
                {/* This is commented out because we don't want to allow anyone to register on our platform.  */}
                {/* We are not deleting register page, because it has logic that can be reutilized later in dashboard signup */}
                {/* <Route path="/register" element={<Register />} /> */}
                <Route path='/terms' element={<Terms />} />
                <Route path='/privacy' element={<Privacy />} />
                <Route path='/demo-request' element={<DemoRequest />} />
                {/* Signed in, but in no company: nothing for the Platform to show. Public so no redirect can loop. */}
                <Route path={NO_PLATFORM_ACCESS_PATH} element={<NoPlatformAccess />} />

                {/* Protected route structure */}
                <Route element={<ProtectedRoute />}>
                    {/* All protected routes go inside this Route */}
                    <Route
                        element={
                            <LiveKitProvider>
                                <SidebarProvider defaultOpen>
                                    <AppSidebar />
                                    <SidebarInset>
                                        <div className='main'>
                                            <TopBar />
                                            <Outlet />
                                        </div>
                                    </SidebarInset>
                                </SidebarProvider>
                            </LiveKitProvider>
                        }
                    >
                        <Route path='/dashboard' element={<Dashboard />} />
                        <Route path='/agentic-workflow' element={<AgenticWorkflow />} />
                        <Route path='/agentic-workflow/:id' element={<AgenticWorkflowDetailLayout />}>
                            <Route index element={<CallAnalyticsPageNew />} />
                            <Route path='live' element={<LiveMonitorPage />} />
                            {/* <Route path='analytics/calls' element={<CallAnalyticsPage />} /> */}
                            <Route path='analytics/calls-new' element={<CallAnalyticsPageNew />} />
                            {/* <Route path='analytics/sms' element={<SMSAnalyticsPage />} /> */}
                            <Route path='analytics/sms' element={<SMSAnalyticsPageNew />} />
                            <Route path='analytics/phone-numbers' element={<WorkflowPhoneNumbersPage />} />
                        </Route>
                        <Route path='/contact-manager' element={<ContactManager />} />
                        {/* TODO: Uncomment this when SMS management is ready */}
                        {/* <Route path='/sms-management' element={<SMSManagement/>}/> */}
                        <Route path='/agentic-workflow-builder/new' element={<AgenticWorkflowBuilder />} />
                        <Route path='/agentic-workflow-builder/edit/:id' element={<AgenticWorkflowBuilder />} />
                        <Route path='/agent-command-center' element={<AgentCommandCenter />} />
                        <Route path='/manager-command-center' element={<ManagerCommandCenter />} />
                        <Route path='/access-manager' element={<AccessManager />} />
                        <Route path='/super-admin-data-entry' element={<SuperAdminDataEntry />} />
                        <Route path='/phone-number-manager' element={<PhoneNumberManager />} />
                        <Route path='/tenant-config' element={<TenantConfigViewer />} />
                        <Route path='/company-products' element={<CompanyProducts />} />
                        <Route path='/people' element={<People />} />
                    </Route>
                </Route>

                <Route path='/error' element={<ErrorPage />} />
                {/* Default redirect */}
                <Route path='*' element={<Navigate to='/dashboard' />} />
            </Routes>
        </Router>
    );
};

export default App;
